"use client";

import { useState, useMemo } from "react";
import { MapPin, Search, ArrowRight, Zap } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const REGIONS = [{
  name: "Hawkesbury Region",
  suburbs: ["South Windsor", "Windsor", "Richmond", "Mulgrave", "Bligh Park", "Pitt Town", "Oakville", "McGraths Hill"]
}, {
  name: "Greater Western Sydney",
  suburbs: ["Penrith", "Blacktown", "Marsden Park", "St Marys", "Schofields", "Riverstone", "Box Hill", "Kellyville"]
}, {
  name: "The Hills District",
  suburbs: ["Castle Hill", "Baulkham Hills", "Rouse Hill", "Norwest", "Bella Vista", "Dural", "Glenhaven", "Galston"]
}, {
  name: "Blue Mountains",
  suburbs: ["Springwood", "Winmalee", "Blaxland", "Glenbrook", "Faulconbridge", "Hazelbrook", "Wentworth Falls", "Katoomba"]
}];
export default function PoweringYourSuburb() {
  const [searchQuery, setSearchQuery] = useState("");
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const filteredRegions = useMemo(() => {
    if (!searchQuery) return REGIONS;
    const lowerQuery = searchQuery.toLowerCase();
    return REGIONS.map(region => ({
      ...region,
      suburbs: region.suburbs.filter(suburb => suburb.toLowerCase().includes(lowerQuery))
    })).filter(region => region.suburbs.length > 0);
  }, [searchQuery]);
  return <section data-section-id="38737" id="find-our-workshop" ref={ref} className={`bg-background py-16 lg:py-24 overflow-hidden transition-opacity duration-700 ${isVisible ? "opacity-100" : "opacity-0"}`}>
      <div className="container mx-auto px-4 md:px-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-start">
          {}
          <div className="relative w-full h-[500px] lg:h-[700px] rounded-xl overflow-hidden shadow-2xl border border-border/50 group">
            {}
            <div className="absolute inset-0 bg-muted">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1599594/img/map_melbourne_service_areas.png" alt="Map of Sydney Service Areas" className="w-full h-full object-cover opacity-70 grayscale transition-transform duration-700 group-hover:scale-105" />
              <div className="absolute inset-0 bg-linear-to-t from-background/90 to-transparent pointer-events-none" />
            </div>

            {}
            <div className="absolute top-1/3 left-1/4 animate-bounce [animation-duration:3s]">
              <MapPin className="size-8 text-accent fill-accent drop-shadow-xl" />
            </div>
            <div className="absolute top-1/2 left-1/2 animate-bounce [animation-duration:4s] [animation-delay:0.5s]">
              <MapPin className="size-8 text-secondary fill-secondary drop-shadow-xl" />
            </div>
            <div className="absolute bottom-1/3 right-1/3 animate-bounce [animation-duration:3.5s] [animation-delay:1s]">
              <MapPin className="size-8 text-accent fill-accent drop-shadow-xl" />
            </div>
            <div className="absolute top-1/4 right-1/4 animate-bounce [animation-duration:4.5s] [animation-delay:1.5s]">
              <MapPin className="size-8 text-secondary fill-secondary drop-shadow-xl" />
            </div>

            {}
            <div className="absolute bottom-6 left-6 right-6 md:left-8 md:right-auto md:w-80">
              <Card className="bg-card/95 backdrop-blur-md border-border/50 shadow-2xl">
                <CardContent className="p-6 flex items-start gap-4">
                  <div className="bg-secondary/10 p-3 rounded-full shrink-0">
                    <Zap className="size-6 text-secondary" />
                  </div>
                  <div>
                    <h3 className="font-bold text-foreground text-lg tracking-tight font-sans">
                      Visit Our Workshop
                    </h3>
                    <p className="text-muted-foreground text-sm mt-1 font-sans">
                      Located in South Windsor, our facility is equipped for major structural and electrical repairs.
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>

          {}
          <div className="flex flex-col h-full">
            <div className="mb-8">
              <h2 className="text-3xl md:text-4xl font-bold tracking-tighter text-foreground mb-4 font-sans">
                Find Our Workshop
              </h2>
              <p className="text-muted-foreground text-lg max-w-xl font-sans leading-relaxed">
                Located in the heart of South Windsor, we are Sydney's premier destination for specialized caravan smash repairs and technical technical installations.
              </p>
            </div>

            {}
            <div className="relative mb-8 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
              <Input type="text" placeholder="Find your nearest area..." className="pl-10 bg-muted/30 border-border focus-visible:ring-secondary rounded-sm" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} />
            </div>

            {}
            <div className="flex-1 overflow-y-auto pr-2 max-h-[500px] custom-scrollbar">
              {filteredRegions.length > 0 ? <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-10">
                  {filteredRegions.map((region, i) => <div key={region.name} data-index={i} className="space-y-4">
                      <h3 className="text-sm font-bold text-secondary flex items-center gap-2 border-b border-border pb-2 uppercase tracking-wider font-mono">
                        {region.name}
                      </h3>
                      <ul className="space-y-2">
                        {region.suburbs.map((suburb, j) => <li key={suburb} data-index={j} className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors text-sm font-sans">
                            <span className="size-1.5 rounded-full bg-accent/60" />
                            {suburb}
                          </li>)}
                      </ul>
                    </div>)}
                </div> : <div className="text-center py-12 border border-dashed border-border rounded-xl bg-muted/20">
                  <MapPin className="size-10 text-muted-foreground mx-auto mb-3 opacity-50" />
                  <p className="text-muted-foreground font-medium font-sans">
                    No matching areas found for "{searchQuery}"
                  </p>
                  <p className="text-sm text-muted-foreground mt-1 font-sans">
                    Don't see your location? Contact us to confirm service range.
                  </p>
                </div>}
            </div>

            {}
            <div className="mt-10 pt-6 border-t border-border">
              <div className="flex flex-col sm:flex-row gap-4 items-center justify-between bg-secondary text-secondary-foreground p-6 rounded-xl shadow-lg">
                <div>
                  <h4 className="font-bold text-lg tracking-tight font-sans">Not sure if we can help?</h4>
                  <p className="text-secondary-foreground/80 text-sm font-sans">
                    Contact our team to discuss your caravan's specific repair needs today.
                  </p>
                </div>
                <Button asChild variant="default" className="bg-accent text-accent-foreground hover:bg-accent/90 shrink-0 rounded-sm font-bold px-8">
                  <Link to="/StartYourQuotePage">
                    Start Your Quote <ArrowRight className="ml-2 size-4" />
                  </Link>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}